/**
 * «Анкета» → Новости. List of the operator's own posts with status, plus an
 * inline editor (title / body / cover) — cover is one media asset shown through
 * its news_cover crop (16:9). Save → draft (POST/PUT) or submit-for-review.
 * Everything is custom UI; no native confirms.
 */
import { useEffect, useRef, useState } from "react";
import {
  type MediaAsset, type NewsInput, type NewsPost, type NewsResult, type NewsStatus,
} from "../../hooks/useOperatorProfile";
import { ANews, APlus, AEdit, ASend, RClose, AMedia } from "./icons";
import { cropPreviewStyle } from "./CropEditor";
import { filterToCss } from "./filters";
import css from "./anketa.module.css";

const STATUS_LABEL: Record<NewsStatus, string> = {
  draft: "Черновик",
  pending: "На проверке",
  published: "Опубликовано",
  rejected: "Отклонено",
};

export function NewsTab({
  news,
  media,
  onSave,
  onToast,
}: {
  news: NewsPost[];
  media: MediaAsset[];
  onSave: (input: NewsInput, id?: number) => Promise<NewsResult>;
  onToast: (msg: string, isErr?: boolean) => void;
}) {
  const [editing, setEditing] = useState<NewsPost | "new" | null>(null);

  return (
    <div>
      <div className={css.secHead}>
        <ANews /> Новости <span className={css.cnt}>({news.length})</span>
      </div>

      {editing ? (
        <NewsEditor
          key={editing === "new" ? "new" : editing.id}
          post={editing === "new" ? null : editing}
          media={media}
          onSave={onSave}
          onToast={onToast}
          onDone={() => setEditing(null)}
        />
      ) : (
        <button type="button" className={css.addBullet} style={{ marginBottom: 12 }} onClick={() => setEditing("new")}>
          <APlus /> Новая новость
        </button>
      )}

      {news.length === 0 && !editing ? (
        <div className={css.empty}>Пока нет новостей. Создай первую — после одобрения её увидят клиенты.</div>
      ) : null}

      {news.map((n) => {
        const cover = media.find((m) => m.id === n.coverMediaId);
        const statusCls = n.status === "published" ? css.published : n.status === "pending" ? css.pending : n.status === "rejected" ? css.rejected : "";
        return (
          <div
            key={n.id}
            className={`${css.newsCard} ${statusCls}`}
            role="button"
            tabIndex={0}
            onClick={() => setEditing(n)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setEditing(n); } }}
          >
            <div className={css.newsCardHead}>
              <span className={css.newsTitle}>{n.title || "Без заголовка"}</span>
              <span className={`${css.newsBadge} ${statusCls}`}>{STATUS_LABEL[n.status]}</span>
            </div>
            {n.body ? <div className={css.newsBody}>{n.body}</div> : null}
            <div className={css.newsFoot}>
              {cover ? (
                <CoverThumb asset={cover} className={css.newsCover} />
              ) : null}
              <span className={css.newsEditCta}><AEdit /> Править</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NewsEditor({
  post,
  media,
  onSave,
  onToast,
  onDone,
}: {
  post: NewsPost | null;
  media: MediaAsset[];
  onSave: (input: NewsInput, id?: number) => Promise<NewsResult>;
  onToast: (msg: string, isErr?: boolean) => void;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(post?.title ?? "");
  const [body, setBody] = useState(post?.body ?? "");
  const [coverId, setCoverId] = useState<number | null>(post?.coverMediaId ?? null);
  const [pickOpen, setPickOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedTick, setSavedTick] = useState(0);

  // Автосохранение черновика: новость не теряется, даже если не нажать кнопку и просто уйти.
  const idRef = useRef<number | null>(post?.id ?? null);
  const savedRef = useRef(JSON.stringify({ t: post?.title ?? "", b: post?.body ?? "", c: post?.coverMediaId ?? null }));
  const valsRef = useRef({ title, body, coverId });
  valsRef.current = { title, body, coverId };
  const savingRef = useRef(false);
  const flushRef = useRef<() => void>(() => {});
  useEffect(() => {
    flushRef.current = () => {
      const v = valsRef.current;
      const cur = JSON.stringify({ t: v.title, b: v.body, c: v.coverId });
      if (cur === savedRef.current || savingRef.current) return;
      if (!v.title.trim() && !v.body.trim() && v.coverId == null) return; // пустую новость не создаём
      savingRef.current = true;
      void onSave({ title: v.title, body: v.body, coverMediaId: v.coverId, submit: false }, idRef.current ?? undefined).then((res) => {
        savingRef.current = false;
        if (res.ok) {
          savedRef.current = cur;
          if (res.id) idRef.current = res.id;
          setSavedTick((n) => n + 1);
          const v2 = valsRef.current;
          if (JSON.stringify({ t: v2.title, b: v2.body, c: v2.coverId }) !== savedRef.current) flushRef.current(); // догнать
        }
      });
    };
  });
  useEffect(() => {
    const cur = JSON.stringify({ t: title, b: body, c: coverId });
    if (cur === savedRef.current) return;
    const t = window.setTimeout(() => flushRef.current(), 800);
    return () => window.clearTimeout(t);
  }, [title, body, coverId]);
  useEffect(() => () => { flushRef.current(); }, []); // дослать черновик при закрытии редактора/размонтировании

  const cover = media.find((m) => m.id === coverId);

  async function commit(submit: boolean) {
    if (busy) return;
    setBusy(true);
    const res = await onSave({ title, body, coverMediaId: coverId, submit }, idRef.current ?? undefined);
    setBusy(false);
    if (res.ok) {
      if (res.id) idRef.current = res.id;
      savedRef.current = JSON.stringify({ t: title, b: body, c: coverId });
      onToast(submit ? "Новость отправлена на проверку" : "Черновик сохранён");
      onDone();
    } else {
      onToast(res.error ?? "Не удалось сохранить", true);
    }
  }

  return (
    <div className={css.editorBlock}>
      <div className={css.editorHead}>
        <span className={css.editorTitle}>{post ? "Правка новости" : "Новая новость"}</span>
        <button type="button" className={css.sheetClose} aria-label="Отменить" onClick={onDone}><RClose /></button>
      </div>

      <div className={css.field}>
        <label className={css.fieldLbl}>Заголовок</label>
        <input className={css.input} value={title} maxLength={160}
          placeholder="О чём новость"
          onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className={css.field}>
        <label className={css.fieldLbl}>Текст</label>
        <textarea className={css.textArea} value={body} maxLength={4000} rows={5}
          placeholder="Текст новости…"
          onChange={(e) => setBody(e.target.value)} />
      </div>

      <label className={css.fieldLbl}>Обложка</label>
      <div className={css.coverPick}>
        {cover ? (
          <CoverThumb asset={cover} className={css.coverThumb} />
        ) : (
          <span className={css.coverEmpty}><AMedia /></span>
        )}
        <button type="button" className={css.linkBtn} onClick={() => setPickOpen(true)}>
          {cover ? "Сменить обложку" : "Выбрать обложку"}
        </button>
        {cover ? (
          <button type="button" className={css.linkBtn} style={{ color: "var(--text-2)" }} onClick={() => setCoverId(null)}>Убрать</button>
        ) : null}
      </div>

      <div className={css.saveBar} style={{ position: "static", margin: 0, padding: 0, background: "none" }}>
        <span className={`${css.saveStatus} ${savedTick > 0 ? css.saved : ""}`} style={{ alignSelf: "center" }}>
          {savedTick > 0 ? "Черновик сохранён" : "Черновик сохраняется сам"}
        </span>
        <button type="button" className={`${css.btn} ${css.primary}`} disabled={busy} onClick={() => commit(true)}>
          <ASend /> {busy ? "…" : "На проверку"}
        </button>
      </div>

      {pickOpen ? (
        <CoverPicker
          media={media}
          current={coverId}
          onPick={(id) => { setCoverId(id); setPickOpen(false); }}
          onClose={() => setPickOpen(false)}
        />
      ) : null}
    </div>
  );
}

/** Cover thumbnail rendered with the asset's news_cover crop + filter via CSS. */
function CoverThumb({ asset, className }: { asset: MediaAsset; className?: string }) {
  const style = cropPreviewStyle(asset.crops.news_cover, filterToCss(asset.filter));
  return (
    <span className={cx(css.previewFrame, className)} style={{ position: "relative" }}>
      <img className={css.previewImg} style={style} src={asset.posterUrl || asset.streamUrl} alt="" />
    </span>
  );
}

/** Bottom-sheet media picker (16:9 grid) for choosing a news cover. */
function CoverPicker({
  media,
  current,
  onPick,
  onClose,
}: {
  media: MediaAsset[];
  current: number | null;
  onPick: (id: number) => void;
  onClose: () => void;
}) {
  const photos = media.filter((m) => m.kind === "photo");
  return (
    <>
      <div className={css.backdrop} onClick={onClose} />
      <div className={css.sheet} role="dialog" aria-modal="true">
        <div className={css.sheetHandle} />
        <div className={css.sheetHead}>
          <span className={css.sheetTitle}>Обложка новости</span>
          <button type="button" className={css.sheetClose} aria-label="Закрыть" onClick={onClose}><RClose /></button>
        </div>
        <div className={css.sheetBody}>
          {photos.length === 0 ? (
            <div className={css.empty}>Сначала загрузи фото во вкладке «Медиа».</div>
          ) : (
            <div className={css.pickGrid}>
              {photos.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={`${css.pickItem} ${current === m.id ? css.on : ""}`}
                  onClick={() => onPick(m.id)}
                >
                  <img src={m.posterUrl || m.streamUrl} alt={m.label} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function cx(...parts: Array<string | undefined | null | false>): string {
  return parts.filter(Boolean).join(" ");
}
